import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    House, Target, Binoculars, Trophy, BookOpen, List, X,
    Chalkboard, GameController, PencilRuler, TreeEvergreen, Compass,
    GraduationCap, SignOut, Student, Gear
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import RangerAvatar from './RangerAvatar';
import { Sheet, SheetContent, SheetTitle } from './ui/sheet';

// Primary items shown directly in the bar (max 5) + a Menu button.
const PRIMARY = {
    child: [
        { label: 'Home', to: '/dashboard', icon: House, testid: 'nav-home' },
        { label: 'Missions', to: '/missions', icon: Target, testid: 'nav-missions' },
        { label: 'Animals', to: '/animals', icon: Binoculars, testid: 'nav-animals' },
        { label: 'Learn', to: '/content', icon: BookOpen, testid: 'nav-content' },
        { label: 'Ranger', to: '/customize', icon: null, testid: 'nav-customize', avatar: true },
    ],
    parent: [
        { label: 'Home', to: '/dashboard', icon: House, testid: 'nav-home' },
        { label: 'Educator', to: '/educator', icon: Chalkboard, testid: 'nav-educator' },
        { label: 'Worksheets', to: '/worksheets', icon: PencilRuler, testid: 'nav-worksheets' },
        { label: 'Games', to: '/games', icon: GameController, testid: 'nav-games' },
        { label: 'Explore', to: '/habitats', icon: TreeEvergreen, testid: 'nav-habitats' },
    ],
    educator: [
        { label: 'Home', to: '/educator', icon: House, testid: 'nav-home' },
        { label: 'Worksheets', to: '/worksheets', icon: PencilRuler, testid: 'nav-worksheets' },
        { label: 'Games', to: '/games', icon: GameController, testid: 'nav-games' },
        { label: 'Explore', to: '/habitats', icon: TreeEvergreen, testid: 'nav-habitats' },
        { label: 'Learn', to: '/content', icon: BookOpen, testid: 'nav-content' },
    ],
};

// Extra destinations surfaced in the slide-up menu drawer.
const MENU = {
    child: [
        { label: 'Leaderboard', to: '/leaderboard', icon: Trophy, testid: 'menu-leaderboard' },
        { label: 'Habitats', to: '/habitats', icon: TreeEvergreen, testid: 'menu-habitats' },
        { label: 'Games', to: '/games', icon: GameController, testid: 'menu-games' },
        { label: 'Adventure', to: '/adventure', icon: Compass, testid: 'menu-adventure' },
        { label: 'My Classroom', to: '/my-classroom', icon: Student, testid: 'menu-classroom' },
    ],
    parent: [
        { label: 'Missions', to: '/missions', icon: Target, testid: 'menu-missions' },
        { label: 'Animals', to: '/animals', icon: Binoculars, testid: 'menu-animals' },
        { label: 'Leaderboard', to: '/leaderboard', icon: Trophy, testid: 'menu-leaderboard' },
        { label: 'Content Hub', to: '/content', icon: BookOpen, testid: 'menu-content' },
        { label: 'Adventure', to: '/adventure', icon: Compass, testid: 'menu-adventure' },
        { label: 'My Classroom', to: '/my-classroom', icon: Student, testid: 'menu-classroom' },
        { label: 'Account', to: '/account', icon: Gear, testid: 'menu-account' },
    ],
    educator: [
        { label: 'Dashboard', to: '/dashboard', icon: GraduationCap, testid: 'menu-dashboard' },
        { label: 'Missions', to: '/missions', icon: Target, testid: 'menu-missions' },
        { label: 'Animals', to: '/animals', icon: Binoculars, testid: 'menu-animals' },
        { label: 'Leaderboard', to: '/leaderboard', icon: Trophy, testid: 'menu-leaderboard' },
        { label: 'Adventure', to: '/adventure', icon: Compass, testid: 'menu-adventure' },
        { label: 'Account', to: '/account', icon: Gear, testid: 'menu-account' },
    ],
};

export default function BottomNav() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    const role = user?.role;
    const primary = PRIMARY[role];
    if (!primary) return null; // admin / unknown roles have no mobile bar

    const menuItems = MENU[role] || [];
    const isActive = (to) => location.pathname === to;
    const emailUnverified = user?.email_verified === false;

    return (
        <>
            <nav className="mobile-nav md:hidden" data-testid="bottom-nav">
                <div className="flex justify-around items-center">
                    {primary.map((item) => {
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`mobile-nav-item ${isActive(item.to) ? 'active' : ''}`}
                                data-testid={item.testid}
                            >
                                {item.avatar ? (
                                    <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-[#1e293b]">
                                        <RangerAvatar config={user?.avatar_config} variant="badge" className="w-full h-full" />
                                    </div>
                                ) : (
                                    <Icon weight={isActive(item.to) ? 'fill' : 'duotone'} size={28} />
                                )}
                                <span className="text-xs font-semibold">{item.label}</span>
                            </Link>
                        );
                    })}
                    <button
                        onClick={() => setOpen(true)}
                        className="mobile-nav-item relative"
                        data-testid="nav-menu"
                    >
                        <List weight="bold" size={28} />
                        {emailUnverified && (
                            <span className="absolute top-1 right-1/4 w-2.5 h-2.5 rounded-full bg-[#ef4444] border-2 border-white" data-testid="menu-unverified-dot" />
                        )}
                        <span className="text-xs font-semibold">Menu</span>
                    </button>
                </div>
            </nav>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="bottom"
                    className="md:hidden rounded-t-3xl border-t-4 border-[#0f172a] bg-[#fefce8] p-0 [&>button]:hidden max-h-[80vh] overflow-y-auto"
                    data-testid="menu-drawer"
                >
                    <div className="flex items-center justify-between px-5 pt-5 pb-3">
                        <SheetTitle className="font-fredoka text-xl font-bold text-[#0f172a]">More</SheetTitle>
                        <button
                            onClick={() => setOpen(false)}
                            className="p-2 rounded-full border-2 border-[#0f172a] bg-white"
                            data-testid="menu-close"
                            aria-label="Close menu"
                        >
                            <X weight="bold" size={18} />
                        </button>
                    </div>

                    <div className="grid grid-cols-3 gap-3 px-5 pb-4">
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    onClick={() => setOpen(false)}
                                    className="relative flex flex-col items-center gap-2 bg-white border-3 border-[#0f172a] rounded-2xl p-4 shadow-[3px_3px_0px_#0f172a] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-all"
                                    data-testid={item.testid}
                                >
                                    {item.to === '/account' && emailUnverified && (
                                        <span className="absolute top-2 right-2 w-3 h-3 rounded-full bg-[#ef4444] border-2 border-white" data-testid="account-unverified-dot" />
                                    )}
                                    <Icon weight="duotone" size={30} className="text-[#16a34a]" />
                                    <span className="text-xs font-bold text-[#0f172a] text-center leading-tight">{item.label}</span>
                                </Link>
                            );
                        })}
                    </div>

                    <div className="px-5 pb-8 pt-2 border-t-2 border-[#0f172a]/10">
                        <button
                            onClick={() => { setOpen(false); logout(); }}
                            className="w-full flex items-center justify-center gap-2 bg-[#0f172a] text-white font-fredoka font-bold py-3 rounded-2xl border-3 border-[#0f172a] shadow-[3px_3px_0px_#fb7185] active:translate-y-0.5 active:shadow-none transition-all"
                            data-testid="menu-logout"
                        >
                            <SignOut weight="bold" size={20} /> Log Out
                        </button>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
